import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

// Pengganti SessionProvider/useSession NextAuth untuk SPA. Sumber sesi tunggal:
// GET /api/auth/me (di-proxy Vite → backend :8790). 401 → user = null.
export interface AuthUser {
  email: string
  name: string
  image: string | null
  role: string | null
  department: string | null
  username: string | null
  isAdmin: boolean
  canSetVisibility: boolean
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me')
      if (res.status === 401 || !res.ok) {
        setUser(null)
        return
      }
      const data = await res.json()
      // Terima bentuk {user:{...}} atau langsung {...}.
      const u = (data?.user ?? data) as Partial<AuthUser> | null
      if (!u || !u.email) {
        setUser(null)
        return
      }
      setUser({
        email: u.email,
        name: u.name ?? u.email,
        image: u.image ?? null,
        role: u.role ?? null,
        department: u.department ?? null,
        username: u.username ?? null,
        isAdmin: u.isAdmin === true,
        canSetVisibility: u.canSetVisibility === true,
      })
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  return (
    <AuthContext.Provider value={{ user, loading, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

// Shim kompatibel next-auth: komponen yang di-port verbatim (AppContext,
// useContentPlans, AppSidebar) memanggil useSession() dan membaca
// session.user.{email,name,image,role,department,username}. Bentuk `data`
// mengikuti NextAuth: null saat belum login.
export function useSession(): { data: { user: AuthUser } | null; status: 'loading' | 'authenticated' | 'unauthenticated' } {
  const { user, loading } = useAuth()
  return {
    data: user ? { user } : null,
    status: loading ? 'loading' : user ? 'authenticated' : 'unauthenticated',
  }
}
